import React, { createContext, useCallback, useEffect, useRef, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

const getNotificationsSocketUrl = () => {
  const url = process.env.REACT_APP_NOTIFICATIONS_WS_URL || '';
  if (!url) return '';
  return url.replace(/^ws:\/\//, 'http://').replace(/^wss:\/\//, 'https://');
};

interface NotificationSocketContextType {
  isConnected: boolean;
  joinRoom: (room: string) => void;
  subscribe: <T = unknown>(event: string, handler: (payload: T) => void) => () => void;
}

const NotificationSocketContext = createContext<NotificationSocketContextType | undefined>(undefined);

interface NotificationSocketProviderProps {
  children: ReactNode;
}

export const NotificationSocketProvider: React.FC<NotificationSocketProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  /** Handlers registrados antes de que exista socket o tras login; evita perder suscripciones. */
  const notificationHandlersRef = useRef(new Set<(payload: unknown) => void>());

  const url = getNotificationsSocketUrl();

  useEffect(() => {
    if (!url || !isAuthenticated) {
      if (!url) console.warn('[NotificationSocket] REACT_APP_NOTIFICATIONS_WS_URL no está definida, no se conecta.');
      if (url && !isAuthenticated) console.warn('[NotificationSocket] No autenticado, no se conecta.');
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    console.log('[NotificationSocket] Conectando a', url, 'path: /notifications');
    const socket = io(url, {
      path: '/notifications',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      autoConnect: true,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      console.log('[NotificationSocket] Conectado, suscribiendo al tópico');
      socket.emit('subscribe-notifications');
    });

    socket.on('subscribed', (data: unknown) => {
      console.log('[NotificationSocket] Suscrito al tópico:', data);
    });

    socket.on('disconnect', (reason) => {
      setIsConnected(false);
      console.log('[NotificationSocket] Desconectado:', reason);
    });

    socket.on('connect_error', (err) => {
      console.warn('[NotificationSocket] Error de conexión:', err.message);
    });

    socket.on('notification', (payload: unknown) => {
      console.log('[NotificationSocket] notification:', payload);
      notificationHandlersRef.current.forEach((fn) => {
        try {
          fn(payload);
        } catch (e) {
          console.error('[NotificationSocket] handler error', e);
        }
      });
    });

    return () => {
      socket.removeAllListeners();
      if (socket.connected) socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [url, isAuthenticated]);

  const joinRoom = useCallback((room: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('subscribe-notifications', room);
    }
  }, []);

  const subscribe = useCallback(<T = unknown>(event: string, handler: (payload: T) => void) => {
    if (event === 'notification') {
      const wrapped = handler as (payload: unknown) => void;
      notificationHandlersRef.current.add(wrapped);
      return () => {
        notificationHandlersRef.current.delete(wrapped);
      };
    }
    const socket = socketRef.current;
    if (!socket) return () => {};
    socket.on(event, handler as (...args: unknown[]) => void);
    return () => {
      socket.off(event, handler as (...args: unknown[]) => void);
    };
  }, []);

  const value: NotificationSocketContextType = {
    isConnected,
    joinRoom,
    subscribe,
  };

  return (
    <NotificationSocketContext.Provider value={value}>
      {children}
    </NotificationSocketContext.Provider>
  );
};

export const useNotificationSocketContext = () => {
  const ctx = React.useContext(NotificationSocketContext);
  if (ctx === undefined) {
    throw new Error('useNotificationSocketContext must be used within NotificationSocketProvider');
  }
  return ctx;
};
